import { type FC, type JSX, type ReactNode } from 'react'
import { FormattedMessage, FormattedNumber } from 'react-intl'

import { type OfferShippingsV1 } from '@redteclab/api/clients/bully'

import { Link } from 'base-ui'

import { useDeliveryInfoExperiment } from '../../../../../experiments/components/ExperimentDeliveryDatesOTC/ExperimentDeliveryDatesOtc'
import {
  EXPERIMENTS_TRACK_EVENT_PDP_DELIVERY_OPTIONS_OTC,
  experimentsTrackEvent,
} from '../../../../../experiments/eventTracking'
import { useGetExperiment } from '../../../../../experiments/hooks'
import { EXPERIMENT_NAME_PDP_DELIVERY_OPTIONS } from '../../../../../experiments/model/EXPERIMENT_NAME'
import { EXPERIMENT_VARIATION } from '../../../../../experiments/model/EXPERIMENT_VARIATION'
import { type ExperimentType } from '../../../../../experiments/model/Experiment.types'
import { useOpenableControls } from '../../../../../generic-hooks'
import {
  type PublicGlobalConfig,
  useGlobalConfigContext,
} from '../../../../../global-config'
import { TENANT } from '../../../../../tenant'
import { productAccessorIsSaeOffer } from '../../../../model/productAccessor/productAccessorIsSaeOffer'
import { productAccessorStockStatusIsAvailable } from '../../../../model/productAccessor/productAccessorStockStatusIsAvailable'
import { shippingAccessorIsFreeShippingMarketplace } from '../../../../model/shippingAccessor/shippingAccessorIsFreeShippingMarketplace'
import { ProductDeliveryInfoDialog } from '../../../ProductDeliveryInfoDialog'
import { useProductPageContext } from '../../../productPageContext'
import { useProductDeliveryPrices } from '../../hooks/useProductDeliveryPrices'

export const determineFreeShipping = (
  isMarketplace: boolean,
  isFreeShippingSae: boolean,
  marketplaceDeliveryCosts: OfferShippingsV1[] | null,
): boolean => {
  return (
    (!isMarketplace && isFreeShippingSae) ||
    (isMarketplace &&
      shippingAccessorIsFreeShippingMarketplace(marketplaceDeliveryCosts))
  )
}

export const getAmount = (value: number, currency: string): JSX.Element => (
  <FormattedNumber
    currency={currency}
    key="amount"
    minimumFractionDigits={Number.isInteger(value) ? 0 : undefined}
    style="currency"
    value={value}
  />
)

const freeShippingMessage = (
  pdpDeliveryOptionsEnabled: boolean,
  isMarketplace: boolean,
): JSX.Element => {
  const className =
    pdpDeliveryOptionsEnabled && !isMarketplace
      ? 'text-xs text-dark-primary-strong'
      : 'font-medium text-dark-success'

  return (
    <span className={className}>
      <FormattedMessage id="buyBox.link.label.aboveFreeShippingLimit" />
    </span>
  )
}

const onClickModal = (
  handleOpen: () => void,
  pdpDeliveryOptionsEnabled: boolean,
  globalContext: PublicGlobalConfig,
): void => {
  handleOpen()
  if (pdpDeliveryOptionsEnabled) {
    experimentsTrackEvent(
      globalContext,
      EXPERIMENTS_TRACK_EVENT_PDP_DELIVERY_OPTIONS_OTC,
    )
  }
}

// eslint-disable-next-line max-lines-per-function, max-statements -- Disabled temporarily for experiment, function complexity will be refactored later
export const ProductBuyBoxDeliveryInfo: FC = () => {
  const globalContext = useGlobalConfigContext()
  const {
    currentOffer,
    currentPrescriptionType,
    offerId,
    product,
    productTemplate,
    shipping: { marketplaceDeliveryCosts },
  } = useProductPageContext()

  const deliveryInfoExperiment = useDeliveryInfoExperiment()
  const deliveryDatesExperiment: ExperimentType =
    deliveryInfoExperiment.deliveryDatesExperiment
  const { tenant } = useGlobalConfigContext()

  const { freeShippingThreshold, isFreeShippingSae, shippingPriceMarketplace } =
    useProductDeliveryPrices({
      currentPrescriptionType,
      offerId,
      product,
      productTemplate,
    })

  const pdpDeliveryOptions = useGetExperiment(
    EXPERIMENT_NAME_PDP_DELIVERY_OPTIONS,
  )

  const { handleClose, handleOpen, isOpen } = useOpenableControls(false)

  if (!productAccessorStockStatusIsAvailable(product) || !currentOffer) {
    return null
  }

  const isMarketplace = !productAccessorIsSaeOffer(currentOffer)

  if (
    determineFreeShipping(
      isMarketplace,
      isFreeShippingSae,
      marketplaceDeliveryCosts,
    )
  ) {
    return freeShippingMessage(pdpDeliveryOptions.isEnabled, isMarketplace)
  }

  const linkToModal = (text: ReactNode): JSX.Element => (
    <Link
      color="inherit"
      key="linkToModal"
      onClick={(): void => {
        onClickModal(handleOpen, pdpDeliveryOptions.isEnabled, globalContext)
      }}
      slots={{ root: 'button' }}
      type="button"
    >
      {text}
    </Link>
  )

  // Handle the PDP delivery options experiment case
  if (
    pdpDeliveryOptions.isEnabled &&
    pdpDeliveryOptions.variant !== EXPERIMENT_VARIATION.DEFAULT &&
    !isMarketplace &&
    currentOffer.offerPrice?.shippingPrice &&
    freeShippingThreshold.amount
  ) {
    const values = {
      amount: getAmount(
        currentOffer.offerPrice.shippingPrice,
        freeShippingThreshold.currency,
      ),
      freeShippingAmount: getAmount(
        freeShippingThreshold.amount,
        freeShippingThreshold.currency,
      ),
      linkToModal,
    }

    return (
      <span className="text-xs text-dark-primary-strong">
        {pdpDeliveryOptions.variant === EXPERIMENT_VARIATION.V3 ||
        pdpDeliveryOptions.variant === EXPERIMENT_VARIATION.V4 ? (
          <FormattedMessage
            id="now.product.shippingCostsShort"
            values={values}
          />
        ) : (
          <FormattedMessage id="now.link.freeShipping" values={values} />
        )}
        <ProductDeliveryInfoDialog isOpen={isOpen} onClose={handleClose} />
      </span>
    )
  }

  return (
    <span>
      {isMarketplace ? (
        <FormattedMessage
          id="buyBox.link.label.belowFreeShippingLimitWithLinkMp_ho"
          values={{
            amount: getAmount(
              shippingPriceMarketplace ?? 0,
              freeShippingThreshold.currency,
            ),
            linkToModal,
          }}
        />
      ) : (
        // Consolidated condition - only display if amount is a number
        typeof freeShippingThreshold.amount === 'number' &&
        ((): JSX.Element => {
          // Check if French experiment conditions are met
          const shouldUseFrenchMessage =
            deliveryDatesExperiment.isEnabled &&
            tenant === TENANT.FR &&
            deliveryDatesExperiment.variant === EXPERIMENT_VARIATION.V2

          return shouldUseFrenchMessage ? (
            <FormattedMessage
              id="buyBox.link.label.belowFreeShippingLimitWithLink_fr"
              values={{
                amount: getAmount(
                  freeShippingThreshold.amount,
                  freeShippingThreshold.currency,
                ),
                linkToModal,
              }}
            />
          ) : (
            <FormattedMessage
              id="buyBox.link.label.belowFreeShippingLimitWithLink_ho"
              values={{
                amount: getAmount(
                  freeShippingThreshold.amount,
                  freeShippingThreshold.currency,
                ),
                linkToModal,
              }}
            />
          )
        })()
      )}
      <ProductDeliveryInfoDialog isOpen={isOpen} onClose={handleClose} />
    </span>
  )
}
